const AUTH_TOKEN_KEYS = ['authToken', 'token', 'akar_session_token'];
const SUPERADMIN_TOKEN_KEYS = ['superadmin_token', 'superAdminToken'];
const DISPATCH_USER_KEY = 'user';
const REFRESH_TOKEN_KEY = 'refreshToken';
const SUPERADMIN_META_KEYS = ['superadmin_user', 'superadmin_token_expires'];
const DISPATCH_SESSION_KEY = 'itraxx_dispatch_session_active';
const SUPERADMIN_SESSION_KEY = 'itraxx_superadmin_session_active';
const AUTH_CONTEXT_MODE_KEY = 'itraxx_auth_context_mode';

const normalizeStoredToken = (value) => {
    if (!value) return null;
    const token = String(value).trim();
    if (!token || token === 'null' || token === 'undefined') return null;
    return token;
};

const readTokenFromStorage = (storage, key) => {
    try {
        return normalizeStoredToken(storage.getItem(key));
    } catch {
        return null;
    }
};

const removeKeysEverywhere = (keys) => {
    for (const key of keys) {
        try {
            sessionStorage.removeItem(key);
        } catch {}
        try {
            localStorage.removeItem(key);
        } catch {}
    }
};

const hasSessionFlag = (key) => {
    try {
        return sessionStorage.getItem(key) === '1';
    } catch {
        return false;
    }
};

const setSessionFlag = (key) => {
    try {
        sessionStorage.setItem(key, '1');
    } catch {}
    try {
        localStorage.removeItem(key);
    } catch {}
};

const clearSessionFlag = (key) => {
    try {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
    } catch {}
};

const getStoredAuthContextModeValue = () => {
    try {
        const raw = sessionStorage.getItem(AUTH_CONTEXT_MODE_KEY);
        return raw === 'dispatch' || raw === 'superadmin' ? raw : null;
    } catch {
        return null;
    }
};

const setStoredAuthContextModeValue = (mode) => {
    try {
        if (mode === 'dispatch' || mode === 'superadmin') {
            sessionStorage.setItem(AUTH_CONTEXT_MODE_KEY, mode);
            return;
        }
        sessionStorage.removeItem(AUTH_CONTEXT_MODE_KEY);
    } catch {}
};

const getStoredTokenFromKeys = (keys, storages = [sessionStorage]) => {
    for (const storage of storages) {
        if (!storage) continue;
        for (const key of keys) {
            const token = readTokenFromStorage(storage, key);
            if (token) {
                return token;
            }
        }
    }
    return null;
};

const getStoredDispatchUserPayload = () => {
    try {
        return sessionStorage.getItem(DISPATCH_USER_KEY);
    } catch {
        return null;
    }
};

const isPlatformSuperAdminUser = (user) =>
    user?.is_superadmin === true && user?.companyId == null && user?.company_id == null;

export function setStoredAuthToken(token) {
    const normalized = normalizeStoredToken(token);
    removeKeysEverywhere(AUTH_TOKEN_KEYS);
    if (!normalized) return;
    try {
        sessionStorage.setItem('authToken', normalized);
    } catch {}
}

export function setStoredRefreshToken(token) {
    const normalized = normalizeStoredToken(token);
    removeKeysEverywhere([REFRESH_TOKEN_KEY]);
    if (!normalized) return;
    try {
        sessionStorage.setItem(REFRESH_TOKEN_KEY, normalized);
    } catch {}
}

export function getStoredRefreshToken() {
    try {
        if (!hasActiveDispatchSession()) return null;
        return normalizeStoredToken(sessionStorage.getItem(REFRESH_TOKEN_KEY));
    } catch {}
    return null;
}

export function setStoredUser(user) {
    removeKeysEverywhere([DISPATCH_USER_KEY]);
    if (user == null) return;
    try {
        sessionStorage.setItem(DISPATCH_USER_KEY, JSON.stringify(user));
    } catch {}
}

export function clearStoredDispatchAuthData() {
    removeKeysEverywhere([...AUTH_TOKEN_KEYS, REFRESH_TOKEN_KEY, DISPATCH_USER_KEY]);
}

export function clearStoredSuperAdminAuthData() {
    removeKeysEverywhere([...SUPERADMIN_TOKEN_KEYS, ...SUPERADMIN_META_KEYS]);
}

export function hasActiveDispatchSession() {
    if (!hasSessionFlag(DISPATCH_SESSION_KEY)) {
        if (!getStoredTokenFromKeys(AUTH_TOKEN_KEYS)) return false;
        setSessionFlag(DISPATCH_SESSION_KEY);
    }
    return hasSessionFlag(DISPATCH_SESSION_KEY);
}

export function hasActiveSuperAdminSession() {
    if (!hasSessionFlag(SUPERADMIN_SESSION_KEY)) {
        const currentUser = getStoredUser();
        if (!isPlatformSuperAdminUser(currentUser) && !getStoredTokenFromKeys(SUPERADMIN_TOKEN_KEYS, [sessionStorage])) {
            return false;
        }
        setSessionFlag(SUPERADMIN_SESSION_KEY);
    }
    return hasSessionFlag(SUPERADMIN_SESSION_KEY);
}

export function markDispatchSessionActive() {
    setSessionFlag(DISPATCH_SESSION_KEY);
}

export function markSuperAdminSessionActive() {
    setSessionFlag(SUPERADMIN_SESSION_KEY);
}

export function clearDispatchSessionActive() {
    clearSessionFlag(DISPATCH_SESSION_KEY);
}

export function clearSuperAdminSessionActive() {
    clearSessionFlag(SUPERADMIN_SESSION_KEY);
}

export function clearAllAuthSessionFlags() {
    clearDispatchSessionActive();
    clearSuperAdminSessionActive();
    setStoredAuthContextModeValue(null);
}

export function getStoredAuthMode() {
    return getStoredAuthContextModeValue();
}

export function getStoredAuthToken() {
    try {
        if (!hasActiveDispatchSession()) return null;
        return getStoredTokenFromKeys(AUTH_TOKEN_KEYS);
    } catch {}
    return null;
}

export function getStoredSuperAdminToken() {
    try {
        if (!hasActiveSuperAdminSession()) return null;
        const explicitToken = getStoredTokenFromKeys(SUPERADMIN_TOKEN_KEYS, [sessionStorage]);
        if (explicitToken) {
            return explicitToken;
        }
        const currentUser = getStoredUser();
        if (isPlatformSuperAdminUser(currentUser)) {
            return getStoredAuthToken();
        }
    } catch {}
    return null;
}

export function buildAuthHeaders(existingHeaders = {}, tokenOverride = null) {
    const token = tokenOverride || getStoredAuthToken();
    if (!token) return { ...existingHeaders };
    return {
        ...existingHeaders,
        Authorization: `Bearer ${token}`
    };
}

export function buildSuperAdminAuthHeaders(existingHeaders = {}, tokenOverride = null) {
    const token = tokenOverride || getStoredSuperAdminToken();
    if (!token) return { ...existingHeaders };
    return {
        ...existingHeaders,
        Authorization: `Bearer ${token}`
    };
}

export function getStoredUser() {
    try {
        if (!hasActiveDispatchSession()) return null;
        const raw = getStoredDispatchUserPayload();
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

export function decodeJwtPayload(tokenOverride = null) {
    try {
        const token = tokenOverride || getStoredAuthToken();
        if (!token) return null;
        const payloadPart = token.split('.')[1];
        if (!payloadPart) return null;
        const json = atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json);
    } catch {
        return null;
    }
}

export function resolveCompanyIdFromToken(explicitCompanyId = null) {
    const parsed = parseInt(String(explicitCompanyId), 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;

    const payload = decodeJwtPayload();
    const raw = payload?.company_id ?? payload?.companyId ?? payload?.companyID ?? (payload?.type === 'company' ? payload?.id : null);
    const parsedToken = parseInt(String(raw), 10);
    if (!Number.isNaN(parsedToken) && parsedToken > 0) return parsedToken;
    return null;
}

// Backward-compatible auth helpers kept so older source files can still build and run
// against the hardened auth-session layer during staged releases.
export function clearDispatchAuthStorage() {
    clearStoredDispatchAuthData();
    clearDispatchSessionActive();
}

export function clearSuperAdminAuthStorage() {
    clearStoredSuperAdminAuthData();
    clearSuperAdminSessionActive();
}

export function storeDispatchUser(userData) {
    setStoredUser(userData);
}

export function storeDispatchAuthState({ authToken, refreshToken, userData } = {}) {
    clearDispatchAuthStorage();

    const normalizedAuthToken = normalizeStoredToken(authToken);
    const normalizedRefreshToken = normalizeStoredToken(refreshToken);

    if (!normalizedAuthToken) {
        return;
    }

    setStoredAuthToken(normalizedAuthToken);
    if (normalizedRefreshToken) {
        setStoredRefreshToken(normalizedRefreshToken);
    }
    if (userData) {
        setStoredUser(userData);
    }
    markDispatchSessionActive();
    setStoredAuthContextModeValue('dispatch');
}

export function storeSuperAdminAuthState({ authToken, userData, tokenExpiresAt = null } = {}) {
    clearSuperAdminAuthStorage();

    const normalizedAuthToken = normalizeStoredToken(authToken);
    if (!normalizedAuthToken) {
        return;
    }

    try {
        sessionStorage.setItem('superadmin_token', normalizedAuthToken);
        if (userData) {
            const payload = JSON.stringify(userData);
            sessionStorage.setItem('superadmin_user', payload);
        }
        if (tokenExpiresAt) {
            sessionStorage.setItem('superadmin_token_expires', String(tokenExpiresAt));
        }
    } catch {}

    markSuperAdminSessionActive();
    setStoredAuthContextModeValue('superadmin');
}

export function getStoredSuperAdminUser() {
    try {
        if (!hasActiveSuperAdminSession()) return null;
        const sessionPayload = sessionStorage.getItem('superadmin_user');
        if (sessionPayload) return JSON.parse(sessionPayload);
    } catch {}
    return null;
}
